"use client";

import { usePathname } from "next/navigation";

import { ProductType } from "@/api_client";
import type { PageImpressionType } from "../events/types/types";

function normalize(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) return "/";

  return `/${segments.join("/")}`;
}

function stripLanguageSegment(pathname: string): string {
  // Paths are like /fi/travel/..., /sv/cart, possibly locale like en-US
  // Remove the first non-empty segment (language) to match product routes consistently
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "/";
  const rest = parts.slice(1).join("/");
  return `/${rest}`;
}

const pageImpressionByProductStage = {
  entry: "EntryProductPage",
  edit: "TopQuestionsPage",
  price: "PricePage",
} as const satisfies Record<string, PageImpressionType>;

const productRoutePattern = /^\/([^/]+)\/([^/]+)$/;
const supportedProductSlugs = new Set<string>(Object.values(ProductType));

type ProductStage = keyof typeof pageImpressionByProductStage;

function isSupportedProductSlug(
  productSlug: string,
): productSlug is ProductType {
  return supportedProductSlugs.has(productSlug);
}

export function getMatchedPageType(
  pathnameRaw: string,
): PageImpressionType | undefined {
  const pathname = normalize(stripLanguageSegment(pathnameRaw));
  const productRouteMatch = productRoutePattern.exec(pathname);

  if (productRouteMatch) {
    const [, productSlug, stage] = productRouteMatch;

    if (isSupportedProductSlug(productSlug)) {
      if (stage in pageImpressionByProductStage) {
        return pageImpressionByProductStage[stage as ProductStage];
      }

      throw new Error(
        `Unhandled webshop product stage "${stage}" for product "${productSlug}" in path "${pathname}".`,
      );
    }

    if (stage in pageImpressionByProductStage) {
      throw new Error(
        `Unhandled webshop product "${productSlug}" in path "${pathname}". Add it to the supported product route handling.`,
      );
    }
  }

  if (pathname === "/checkout") return "CheckoutPage";
  if (pathname === "/confirmation") return "ConfirmationPage";
  if (pathname === "/cart") return "CartPage";
}

export function useGetMatchedPageType(): PageImpressionType | undefined {
  const pathnameRaw = usePathname() || "/";
  return getMatchedPageType(pathnameRaw);
}
